import React, { useEffect, useMemo, useRef, useState } from "react";
import TablePagination from "../../components/shared/TablePagination";
import {
  useFetchExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  type ExpenseRecord,
  type CreateExpensePayload,
} from "../../store/api/expensesApi";
import { getApiErrorMessage } from "../../store/api/baseApi";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { exportToExcel } from "../../utils/exportExcel";
import "./admin.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmt = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

const parseDate = (d: string) => {
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};
const isSameDay = (d: string, ref: Date) => {
  const dt = parseDate(d);
  return (
    dt &&
    dt.getFullYear() === ref.getFullYear() &&
    dt.getMonth() === ref.getMonth() &&
    dt.getDate() === ref.getDate()
  );
};
const isSameMonth = (d: string, ref: Date) => {
  const dt = parseDate(d);
  return (
    dt &&
    dt.getFullYear() === ref.getFullYear() &&
    dt.getMonth() === ref.getMonth()
  );
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const fmtDate = (d: string) => {
  const dt = parseDate(d);
  if (!dt) return d;
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
};

type FormState = {
  name: string;
  purpose: string;
  date: string;
  amount: string;
};
const emptyForm = (): FormState => ({
  name: "",
  purpose: "",
  date: todayStr(),
  amount: "",
});

// ─── Summary Card ─────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{
  label: string;
  sub: string;
  amount: number;
  icon: string;
  color: string;
}> = ({ label, sub, amount, icon, color }) => (
  <article className={`exp-summary-card exp-summary-card--${color}`}>
    <div className='exp-summary-icon'>
      <i className={icon}></i>
    </div>
    <div className='exp-summary-body'>
      <p className='exp-summary-label'>{label}</p>
      <h3 className='exp-summary-value'>{fmt(amount)}</h3>
      <span className='exp-summary-sub'>{sub}</span>
    </div>
  </article>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const ExpenseManager: React.FC = () => {
  const { data: expenses = [], isLoading, error } = useFetchExpensesQuery();
  const [createExpense, { isLoading: creating }] = useCreateExpenseMutation();
  const [updateExpense, { isLoading: updating }] = useUpdateExpenseMutation();
  const [deleteExpense, { isLoading: deleting }] = useDeleteExpenseMutation();

  // ── Form state ───────────────────────────────────────────────────────────────
  const [editingExpense, setEditingExpense] = useState<ExpenseRecord | null>(
    null,
  );
  const [form, setForm] = useState<FormState>(emptyForm());
  const [validated, setValidated] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef<HTMLDivElement>(null);

  // ── Filter state ─────────────────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const now = useMemo(() => new Date(), []);

  // ── Totals ───────────────────────────────────────────────────────────────────
  const totalExpense = useMemo(
    () => expenses.reduce((s, e) => s + e.amount, 0),
    [expenses],
  );
  const dayTotal = useMemo(
    () =>
      expenses
        .filter((e) => isSameDay(e.date, now))
        .reduce((s, e) => s + e.amount, 0),
    [expenses, now],
  );
  const monthTotal = useMemo(
    () =>
      expenses
        .filter((e) => isSameMonth(e.date, now))
        .reduce((s, e) => s + e.amount, 0),
    [expenses, now],
  );

  const availableYears = useMemo(() => {
    const yrs = new Set(
      expenses.map((e) => parseDate(e.date)?.getFullYear()).filter(Boolean),
    );
    return Array.from(yrs).sort(
      (a, b) => (b as number) - (a as number),
    ) as number[];
  }, [expenses]);

  // ── Filtered list ─────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...expenses].sort((a, b) => (b.date > a.date ? 1 : -1));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.purpose.toLowerCase().includes(q),
      );
    }
    if (filterYear) {
      list = list.filter((e) => {
        const dt = parseDate(e.date);
        return dt && dt.getFullYear() === Number(filterYear);
      });
    }
    if (filterMonth) {
      list = list.filter((e) => {
        const dt = parseDate(e.date);
        return dt && dt.getMonth() === Number(filterMonth);
      });
    }
    return list;
  }, [expenses, search, filterYear, filterMonth]);

  const filteredTotal = useMemo(
    () => filtered.reduce((s, e) => s + e.amount, 0),
    [filtered],
  );

  useEffect(() => { setCurrentPage(1); }, [search, filterYear, filterMonth]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedExpenses = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ── Form helpers ──────────────────────────────────────────────────────────────
  const setField = (key: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const startEdit = (expense: ExpenseRecord) => {
    setEditingExpense(expense);
    setForm({
      name: expense.name,
      purpose: expense.purpose,
      date: expense.date,
      amount: String(expense.amount),
    });
    setValidated(false);
    setSubmitError("");
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEdit = () => {
    setEditingExpense(null);
    setForm(emptyForm());
    setValidated(false);
    setSubmitError("");
  };

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);
    if (!e.currentTarget.checkValidity()) return;

    const payload: CreateExpensePayload = {
      name: form.name.trim(),
      purpose: form.purpose.trim(),
      date: form.date,
      amount: parseFloat(form.amount),
    };

    setSubmitError("");
    try {
      if (editingExpense) {
        await updateExpense({ id: editingExpense.id, ...payload }).unwrap();
        setEditingExpense(null);
      } else {
        await createExpense(payload).unwrap();
      }
      setForm(emptyForm());
      setValidated(false);
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────────
  const handleDelete = async (exp: ExpenseRecord) => {
    if (!window.confirm(`Delete "${exp.name}"? This cannot be undone.`)) return;
    if (editingExpense?.id === exp.id) cancelEdit();
    try {
      await deleteExpense(exp.id).unwrap();
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err as FetchBaseQueryError));
    }
  };

  const isSaving = creating || updating;
  const apiError = error
    ? getApiErrorMessage(error as FetchBaseQueryError)
    : "";
  const isEditMode = Boolean(editingExpense);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <section className='admin-page'>
      {apiError && <div className='alert alert-danger'>{apiError}</div>}

      {/* ── Summary Cards ──────────────────────────────────────────────────── */}
      <div className='exp-summary-grid'>
        <SummaryCard
          label='Total Expense'
          sub={`${expenses.length} record${expenses.length !== 1 ? "s" : ""} overall`}
          amount={totalExpense}
          icon='bi bi-wallet2'
          color='teal'
        />
        <SummaryCard
          label="Today's Expenses"
          sub={new Date().toLocaleDateString("en-IN", {
            weekday: "long",
            day: "numeric",
            month: "long",
          })}
          amount={dayTotal}
          icon='bi bi-sun-fill'
          color='blue'
        />
        <SummaryCard
          label='This Month'
          sub={new Date().toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric",
          })}
          amount={monthTotal}
          icon='bi bi-calendar-month-fill'
          color='violet'
        />
      </div>

      {/* ── Add / Edit Form ─────────────────────────────────────────────────── */}
      <div
        ref={formRef}
        style={{
          background: "#fff",
          borderRadius: 14,
          border: "1.5px solid #e2e8f0",
          overflow: "hidden",
          marginBottom: 20,
          boxShadow: "0 2px 12px #0000000a",
        }}
      >
        {/* Form header banner */}
        <div
          style={{
            background: isEditMode
              ? "linear-gradient(135deg, #78350f, #d97706, #f59e0b)"
              : "linear-gradient(135deg, #134e4a, #0d9488, #14b8a6)",
            padding: "8px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i
                className={
                  isEditMode ? "bi bi-pencil-square" : "bi bi-wallet-fill"
                }
                style={{ color: "#fff", fontSize: 13 }}
              />
            </div>
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  color: "#fff",
                  lineHeight: 1.2,
                }}
              >
                {isEditMode ? "Edit Expense" : "Record New Expense"}
              </div>
              <div
                style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}
              >
                {isEditMode
                  ? `Updating: "${editingExpense?.name}"`
                  : "Fill in the details below to log an expense"}
              </div>
            </div>
          </div>
          {isEditMode && (
            <span
              style={{
                background: "rgba(255,255,255,0.22)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: 20,
                padding: "3px 12px",
                fontSize: "0.74rem",
                fontWeight: 600,
              }}
            >
              Editing
            </span>
          )}
        </div>

        {/* Form body */}
        <div style={{ padding: "18px 20px 16px" }}>
          {submitError && (
            <div className='alert alert-danger py-2 mb-3'>{submitError}</div>
          )}

          <form
            noValidate
            className={validated ? "was-validated" : ""}
            onSubmit={handleSubmit}
          >
            <div className='row g-3 align-items-end'>
              {/* Expense Name */}
              <div className='col-md-6'>
                <label
                  className='form-label mb-0 fw-semibold'
                  htmlFor='expName'
                  style={{ fontSize: "0.84rem", color: "#334155" }}
                >
                  Expense Name <span className='text-danger'>*</span>
                  <small
                    className='d-block fw-normal text-muted'
                    style={{ fontSize: "0.71rem" }}
                  >
                    Short name identifying this expense entry
                  </small>
                </label>
                <div style={{ marginTop: 5 }}>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text'>
                      <i className='bi bi-tag-fill' style={{ fontSize: 12 }} />
                    </span>
                    <input
                      id='expName'
                      className='form-control form-control-sm'
                      title='Expense name'
                      value={form.name}
                      minLength={2}
                      required
                      onChange={(e) => setField("name", e.target.value)}
                    />
                  </div>
                  <div className='invalid-feedback'>
                    Enter a name (min 2 chars).
                  </div>
                </div>
              </div>

              {/* Purpose */}
              <div className='col-md-6'>
                <label
                  className='form-label mb-0 fw-semibold'
                  htmlFor='expPurpose'
                  style={{ fontSize: "0.84rem", color: "#334155" }}
                >
                  Purpose <span className='text-danger'>*</span>
                  <small
                    className='d-block fw-normal text-muted'
                    style={{ fontSize: "0.7rem", marginTop: 1 }}
                  >
                    What this expense was spent on
                  </small>
                </label>
                <div style={{ marginTop: 5 }}>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text'>
                      <i className='bi bi-card-text' style={{ fontSize: 12 }} />
                    </span>
                    <input
                      id='expPurpose'
                      className='form-control form-control-sm'
                      title='Expense purpose'
                      value={form.purpose}
                      minLength={2}
                      required
                      onChange={(e) => setField("purpose", e.target.value)}
                    />
                  </div>
                  <div className='invalid-feedback'>
                    Describe the purpose (min 2 chars).
                  </div>
                </div>
              </div>

              {/* Date */}
              <div className='col-md-6'>
                <label
                  className='form-label mb-0 fw-semibold'
                  htmlFor='expDate'
                  style={{ fontSize: "0.84rem", color: "#334155" }}
                >
                  Date of Expense <span className='text-danger'>*</span>
                  <small
                    className='d-block fw-normal text-muted'
                    style={{ fontSize: "0.7rem", marginTop: 1 }}
                  >
                    When this expense was incurred
                  </small>
                </label>
                <div style={{ marginTop: 5 }}>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text'>
                      <i className='bi bi-calendar-date-fill' style={{ fontSize: 12 }} />
                    </span>
                    <input
                      id='expDate'
                      type='date'
                      className='form-control form-control-sm'
                      title='Date of expense'
                      value={form.date}
                      required
                      onChange={(e) => setField("date", e.target.value)}
                    />
                  </div>
                  <div className='invalid-feedback'>Select a date.</div>
                </div>
              </div>

              {/* Amount */}
              <div className='col-md-6'>
                <label
                  className='form-label mb-0 fw-semibold'
                  htmlFor='expAmount'
                  style={{ fontSize: "0.84rem", color: "#334155" }}
                >
                  Amount (₹) <span className='text-danger'>*</span>
                  <small
                    className='d-block fw-normal text-muted'
                    style={{ fontSize: "0.7rem", marginTop: 1 }}
                  >
                    Total rupee amount for this expense
                  </small>
                </label>
                <div style={{ marginTop: 5 }}>
                  <div className='input-group input-group-sm'>
                    <span className='input-group-text' style={{ fontWeight: 700 }}>
                      ₹
                    </span>
                    <input
                      id='expAmount'
                      type='number'
                      className='form-control form-control-sm'
                      title='Expense amount in rupees'
                      value={form.amount}
                      min={1}
                      step='0.01'
                      required
                      onChange={(e) => setField("amount", e.target.value)}
                    />
                  </div>
                  <div className='invalid-feedback'>
                    Enter a valid amount greater than zero.
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className='col-12'>
                <div
                  style={{
                    borderTop: "1.5px dashed #e2e8f0",
                    paddingTop: 14,
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <button
                    type='submit'
                    disabled={isSaving}
                    style={{
                      background: isEditMode
                        ? "linear-gradient(135deg, #d97706, #f59e0b)"
                        : "linear-gradient(135deg, #0d9488, #14b8a6)",
                      border: "none",
                      color: "#fff",
                      borderRadius: 8,
                      padding: "7px 20px",
                      fontSize: "0.83rem",
                      fontWeight: 700,
                      cursor: isSaving ? "wait" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      boxShadow: isEditMode
                        ? "0 3px 10px #d9770633"
                        : "0 3px 10px #0d948833",
                    }}
                  >
                    {isSaving ? (
                      <>
                        <span
                          className='spinner-border spinner-border-sm'
                          role='status'
                          aria-hidden='true'
                        />{" "}
                        Saving…
                      </>
                    ) : (
                      <>
                        <i
                          className={
                            isEditMode
                              ? "bi bi-check2-square"
                              : "bi bi-plus-circle-fill"
                          }
                        />
                        {isEditMode ? "Update Expense" : "Add Expense"}
                      </>
                    )}
                  </button>
                  <button
                    type='button'
                    onClick={() => {
                      setForm(emptyForm());
                      setValidated(false);
                    }}
                    disabled={isSaving}
                    style={{
                      background: "#f1f5f9",
                      border: "1.5px solid #cbd5e1",
                      color: "#475569",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontSize: "0.83rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <i className='bi bi-arrow-counterclockwise' /> Reset
                  </button>
                  {isEditMode && (
                    <button
                      type='button'
                      onClick={cancelEdit}
                      disabled={isSaving}
                      style={{
                        background: "#f1f5f9",
                        border: "1.5px solid #cbd5e1",
                        color: "#64748b",
                        borderRadius: 8,
                        padding: "6px 16px",
                        fontSize: "0.83rem",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  )}
                  {form.name || form.purpose || form.amount ? (
                    <div
                      style={{
                        marginLeft: "auto",
                        fontSize: "0.74rem",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <i className='bi bi-info-circle' />
                      {form.name && form.purpose && form.amount
                        ? "Ready to save"
                        : "Fill required fields"}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── Expense List (full width) ──────────────────────────────────────── */}
      <div className='card border-0 shadow-sm rounded-4'>
        {/* Filter bar */}
        <div className='d-flex flex-wrap gap-3 align-items-center p-3 border-bottom'>
          <div style={{ minWidth: 220 }}>
            <input
              className='form-control form-control-sm'
              title='Search expenses by name or purpose'
              aria-label='Search by name or purpose'
              placeholder='Search by name or purpose…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 130 }}>
            <select
              className='form-select form-select-sm'
              aria-label='Filter by year'
              title='Filter by year'
              value={filterYear}
              onChange={(e) => { setFilterYear(e.target.value); setFilterMonth(""); }}
            >
              <option value=''>All Years</option>
              {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 130 }}>
            <select
              className='form-select form-select-sm'
              aria-label='Filter by month'
              title='Filter by month'
              value={filterMonth}
              disabled={!filterYear}
              onChange={(e) => setFilterMonth(e.target.value)}
            >
              <option value=''>All Months</option>
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </div>
          {(search || filterYear || filterMonth) && (
            <button type='button' className='btn btn-outline-secondary btn-sm' title='Clear filters'
              onClick={() => { setSearch(""); setFilterYear(""); setFilterMonth(""); }}>
              <i className='bi bi-x-lg' />
            </button>
          )}
          <button
            type='button'
            className='btn btn-outline-success btn-sm d-inline-flex align-items-center gap-2'
            onClick={() => exportToExcel(filtered.map((e, i) => ({ "#": i + 1, "Expense Name": e.name, Purpose: e.purpose, Date: fmtDate(e.date), "Amount (₹)": e.amount })), "Expenses")}
          >
            <i className='bi bi-file-earmark-excel' /> Export
          </button>
          <span className='small text-muted ms-auto'>
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            {(filterYear || filterMonth || search) && (
              <span className='ms-2 text-success fw-semibold'>· {fmt(filteredTotal)}</span>
            )}
          </span>
        </div>
        <div className='card-body p-4'>
          {/* Table */}
          {isLoading ? (
            <div className='exp-loading'>Loading expenses…</div>
          ) : filtered.length === 0 ? (
            <div className='dash-empty-state'>
              <i className='bi bi-receipt fs-2 d-block mb-2'></i>
              {search || filterYear
                ? "No records match your filter."
                : "No expenses added yet. Use the form above to start."}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className='table-responsive d-none d-md-block'>
                <table className='table align-middle mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th>#</th>
                      <th>Expense Name</th>
                      <th>Purpose</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th className='text-end'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedExpenses.map((exp, idx) => (
                      <tr
                        key={exp.id}
                        className={
                          editingExpense?.id === exp.id ? "table-warning" : ""
                        }
                      >
                        <td className='text-muted'>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                        <td className='fw-semibold'>{exp.name}</td>
                        <td className='text-muted'>{exp.purpose}</td>
                        <td>
                          <span className='exp-date-badge'>
                            {fmtDate(exp.date)}
                          </span>
                        </td>
                        <td className='fw-bold text-dark'>{fmt(exp.amount)}</td>
                        <td className='text-end'>
                          <div className='d-inline-flex gap-2'>
                            <button
                              type='button'
                              className='btn btn-outline-primary btn-sm admin-icon-btn'
                              title={`Edit ${exp.name}`}
                              aria-label={`Edit ${exp.name}`}
                              disabled={deleting}
                              onClick={() => startEdit(exp)}
                            >
                              <i className='bi bi-pencil-fill'></i>
                            </button>
                            <button
                              type='button'
                              className='btn btn-outline-danger btn-sm admin-icon-btn'
                              title={`Delete ${exp.name}`}
                              aria-label={`Delete ${exp.name}`}
                              disabled={deleting}
                              onClick={() => handleDelete(exp)}
                            >
                              <i className='bi bi-trash-fill'></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className='exp-tfoot-total'>
                      <td colSpan={4} className='text-end fw-bold text-muted'>
                        Total Shown
                      </td>
                      <td className='fw-bold text-dark'>
                        {fmt(filteredTotal)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Mobile cards */}
              <div className='d-md-none exp-mobile-list'>
                {pagedExpenses.map((exp, idx) => (
                  <div
                    key={exp.id}
                    className={`exp-mobile-card${editingExpense?.id === exp.id ? " border-warning" : ""}`}
                  >
                    <div className='exp-mobile-top'>
                      <span className='exp-mobile-index'>{(currentPage - 1) * PAGE_SIZE + idx + 1}</span>
                      <span className='fw-semibold flex-grow-1'>
                        {exp.name}
                      </span>
                      <span className='fw-bold'>{fmt(exp.amount)}</span>
                    </div>
                    <div className='exp-mobile-meta'>
                      <span className='text-muted'>{exp.purpose}</span>
                      <span className='exp-date-badge'>
                        {fmtDate(exp.date)}
                      </span>
                    </div>
                    <div className='admin-table-actions mt-2'>
                      <button
                        type='button'
                        className='btn btn-outline-primary btn-sm admin-icon-btn'
                        title={`Edit ${exp.name}`}
                        aria-label={`Edit ${exp.name}`}
                        onClick={() => startEdit(exp)}
                        disabled={deleting}
                      >
                        <i className='bi bi-pencil-fill'></i>
                      </button>
                      <button
                        type='button'
                        className='btn btn-outline-danger btn-sm admin-icon-btn'
                        title={`Delete ${exp.name}`}
                        aria-label={`Delete ${exp.name}`}
                        onClick={() => handleDelete(exp)}
                        disabled={deleting}
                      >
                        <i className='bi bi-trash-fill'></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </section>
  );
};

export default ExpenseManager;
